'use client'

import { ext } from '@/utils/linkTarget'
import { useState, useRef } from 'react'
import Layout from '@/components/templates/layout'
import Image from 'next/image'
import { urlForImage } from '@/sanity/lib/image'
import { clientFetch } from '@/sanity/lib/client'
import { getFileAsset } from '@sanity/asset-utils'
import { useMediaQuery } from '@mui/material'
import { PortableText } from 'next-sanity'
import editorRule from '@/components/atoms/editorRule'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

// Deep Blue colour tokens (matches open.mahimabali.com)
const C = {
  bg:      '#0E1B28',
  panel:   '#17293B',
  darkest: '#0F1D2B',
  ink:     '#F0ECE4',
  dune:    '#D2CDBF',
  khaki:   '#93A0AB',
  gold:    '#C9A96E',
  line:    'rgba(210,205,191,0.22)',
}

// ── Cover ─────────────────────────────────────────────────────────────────────

const VideoCover = ({ cover }) => {
  const isMobile = useMediaQuery('(max-width:767px)')
  const videoRef = useRef(null)

  const posterSrc = isMobile
    ? urlForImage(cover.imagePlaceholder.mobile).url()
    : urlForImage(cover.imagePlaceholder.desktop).url()

  const videoSrc = isMobile
    ? getFileAsset(cover.video.mobile, clientFetch.config()).url
    : getFileAsset(cover.video.desktop, clientFetch.config()).url

  return (
    <video
      ref={videoRef}
      autoPlay playsInline muted loop
      poster={posterSrc}
      className="absolute inset-0 w-full h-full object-cover"
    >
      <source src={videoSrc} type="video/mp4" />
    </video>
  )
}

const HeroCover = ({ cover }) => {
  if (!cover) return <div className="absolute inset-0" style={{ background: C.panel }} />
  if (cover.option === 'video' && cover.video) return <VideoCover cover={cover} />
  if (cover.option === 'image' && cover.image?.desktop?.asset) {
    return (
      <>
        <Image src={urlForImage(cover.image.desktop).url()} alt={cover.image.desktop.alt || 'Mahima Open 2026'}
          fill placeholder="blur" blurDataURL={cover.image.desktop.metadata?.lqip}
          priority className="hidden md:block object-cover" quality={90} />
        <Image src={urlForImage(cover.image.mobile).url()} alt={cover.image.mobile.alt || 'Mahima Open 2026'}
          fill placeholder="blur" blurDataURL={cover.image.mobile.metadata?.lqip}
          priority className="md:hidden object-cover" quality={90} />
      </>
    )
  }
  return <div className="absolute inset-0" style={{ background: C.panel }} />
}

// ── Hero Icon ─────────────────────────────────────────────────────────────────

const HeroIcon = () => (
  <svg viewBox="0 0 300 200" className="w-[100px] md:w-[120px] h-auto mb-6 md:mb-10" fill="none">
    <path fill="#F2EEE6" d="M186.33,58.37c-2.69,0-4.93,2.23-4.93,4.92s2.24,4.92,4.93,4.92,4.92-2.23,4.92-4.92-2.23-4.92-4.92-4.92" />
    <path fill="#F2EEE6" d="M286.06,120.08v10.55c-.99.05-2.01.09-3,.12-43.97,1.82-89.26-8.1-124.46-35.18-7.09-4.66-17.86-14.41-26.65-11.16-10.7,3.42-10.72,20.12-22.46,22.59-10.58,1.61-17.15-9.61-22.05-16.96-5.3-8.32-11.06-17.87-18.23-24.54-1.7-1.58-2.91-2.05-3.86-2.11,0,0-.53-.03-1.08.06-5.24.99-5.73,11.41-11.88,26.7-2.01,4.99-3.86,8.64-4.81,10.41-2.51,4.75-5.59,10.49-11.18,16.75-6.17,6.93-11.71,10.33-13.01,11.12-1.64.98-3.06,1.72-4.06,2.21v-10.37c.86-.46,1.72-.98,2.54-1.53,6.51-4.15,11.47-9.89,16.55-16.89,4.98-6.73,8.99-14.11,12.1-21.96,2.63-6.09,3.83-11,7.1-17.08,1.33-2.5,4.47-4.75,7.56-4.38,8.45.83,14.51,11.99,18.83,18.31,2.31,3.67,4.63,7.3,7.15,10.74,3.61,4.84,7.44,9.95,12.74,12.55,3.12,1.32,5.47.79,7.62-1.91,2.44-3.11,4.22-7.25,6.72-10.7,4.1-6.28,11.99-10.81,19.65-9.14,4.79.98,8.72,3.57,12.52,6.25,10.35,7.81,20.81,15.37,32.47,20.94,31.72,15.66,68.33,19.64,103.13,14.61h.04Z" />
  </svg>
)

// ── Film Block ────────────────────────────────────────────────────────────────

const FilmBlock = ({ promoVideo, eventInfo, hero }) => {
  const [playing, setPlaying] = useState(false)
  const videoRef = useRef(null)

  const youtubeUrl = promoVideo?.youtubeUrl
  const date = hero?.dateBadge || eventInfo?.date || '5–8 November'
  const location = hero?.locationText || eventInfo?.location || 'Mahima Ubud – Sayan Valley'

  const embedUrl = youtubeUrl
    ? (youtubeUrl.includes('youtu.be/')
        ? `https://www.youtube-nocookie.com/embed/${youtubeUrl.split('youtu.be/')[1]}?autoplay=1`
        : `https://www.youtube-nocookie.com/embed/${new URLSearchParams(new URL(youtubeUrl).search).get('v')}?autoplay=1`)
    : null

  const handlePlay = () => {
    if (youtubeUrl) { setPlaying(true); return }
    if (videoRef.current) {
      videoRef.current.setAttribute('controls', '')
      videoRef.current.play()
      setPlaying(true)
    }
  }

  return (
    <section style={{ background: C.bg, padding: '96px 0 104px' }}>
      <div className="w-full max-w-[980px] mx-auto px-6">
        <div className="relative p-[14px]" style={{ border: `1.5px solid rgba(226,222,210,0.4)` }}>
          {/* Inner inset border */}
          <div className="absolute pointer-events-none z-[2]" style={{ inset: 6, border: '1px solid rgba(226,222,210,0.18)' }} />

          {/* Corner labels */}
          <span className="absolute top-[20px] left-[24px] z-[3] font-chivoMono uppercase tracking-[4px] text-[10px] md:text-[11px]" style={{ color: C.khaki }}>
            Mahima Open
          </span>
          <span className="absolute top-[20px] right-[24px] z-[3] font-chivoMono uppercase tracking-[4px] text-[10px] md:text-[11px]" style={{ color: C.khaki }}>
            Padel &amp; Tennis
          </span>
          <span className="absolute bottom-[20px] left-[24px] z-[3] font-chivoMono uppercase tracking-[3px] text-[9px] md:text-[10px]" style={{ color: `rgba(210,205,191,0.5)` }}>
            {location}
          </span>
          <span className="absolute bottom-[20px] right-[24px] z-[3] font-chivoMono uppercase tracking-[3px] text-[9px] md:text-[10px]" style={{ color: `rgba(210,205,191,0.5)` }}>
            {date}
          </span>

          {/* Video */}
          <div className="relative w-full" style={{ aspectRatio: '848 / 480' }}>
            {embedUrl && playing ? (
              <iframe src={embedUrl} className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title="Mahima Open Film" />
            ) : (
              <video ref={videoRef} preload="none" playsInline poster="/assets/teaser-poster.jpg"
                className="absolute inset-0 w-full h-full object-cover" style={{ background: C.darkest }}>
                <source src="/assets/mahima-open-teaser.mp4" type="video/mp4" />
              </video>
            )}

            {/* Play overlay */}
            {!playing && (
              <button onClick={handlePlay} aria-label="Play the Mahima Open film"
                className="absolute inset-0 z-[4] flex flex-col items-center justify-center gap-4 w-full border-none cursor-pointer transition-colors duration-300"
                style={{ background: 'rgba(10,18,28,0.25)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(10,18,28,0.45)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(10,18,28,0.25)'}
              >
                <span className="flex items-center justify-center rounded-full transition-transform duration-300 hover:scale-105"
                  style={{ width: 84, height: 84, border: '1.5px solid rgba(201,169,110,0.85)', background: 'rgba(10,18,28,0.55)', paddingLeft: 5 }}>
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="none">
                    <path d="M8 5.5v13l11-6.5z" fill="#E8C878" />
                  </svg>
                </span>
                <span className="font-chivoMono uppercase tracking-[4px] text-[11px] md:text-[13px]" style={{ color: C.dune }}>
                  Play the film
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ── Photo Carousel ────────────────────────────────────────────────────────────

const PhotoCarousel = ({ photos }) => {
  if (!photos?.length) return null
  return (
    <section className="bg-background pb-largeMobile lg:pb-largeDesktop">
      <Swiper
        slidesPerView={1.2} spaceBetween={8}
        breakpoints={{
          640:  { slidesPerView: 2.1, spaceBetween: 10 },
          1024: { slidesPerView: 3.1, spaceBetween: 10 },
          1440: { slidesPerView: 3.6, spaceBetween: 10 },
        }}
        className="w-full"
      >
        {photos.map((photo, i) => (
          <SwiperSlide key={i}>
            <div className="relative w-full aspect-[4/5]">
              {photo?.asset?._ref ? (
                <Image src={urlForImage(photo).url()} alt={photo.alt || 'Mahima Open'} fill
                  placeholder={photo.metadata?.lqip ? 'blur' : 'empty'} blurDataURL={photo.metadata?.lqip}
                  className="object-cover" />
              ) : (
                <div className="absolute inset-0" style={{ background: C.panel }} />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

// ── Section divider ───────────────────────────────────────────────────────────

const SectionDivider = () => (
  <div style={{ width: 40, height: 1, background: C.khaki, margin: '0 0 40px' }} />
)

// ── Page ──────────────────────────────────────────────────────────────────────

const MahimaOpenPage = ({ mahimaOpenAPI, footerAPI, settingAPI }) => {
  const d = mahimaOpenAPI || {}
  const info = d.eventInfo || {}
  const hero = d.hero || {}
  const registerLink = d.registerLink || 'https://open.mahimabali.com'
  const registerButtonText = d.registerButtonText || 'Register Now'
  const ctaRow1 = d.registerCtaRow1 || 'Mahima'
  const ctaRow2 = d.registerCtaRow2 || 'Open'

  return (
    <Layout settingAPI={settingAPI} footerAPI={footerAPI} isEventPage>

      {/* ── Hero ── */}
      <section className="relative w-full min-h-[100svh] overflow-hidden flex flex-col items-center justify-center text-center"
        style={{ background: C.panel, padding: '120px 24px 90px' }}>
        <HeroCover cover={d.cover} />

        {/* Bottom gradient fade into page bg */}
        <div className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none z-[2]"
          style={{ background: `linear-gradient(to bottom, transparent, ${C.bg})` }} />

        {/* Decorative circle */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
          style={{ width: 600, height: 600, border: '1px solid rgba(210,205,191,0.06)' }} />

        <div className="relative z-[3] flex flex-col items-center">
          <HeroIcon />

          {/* Date pill */}
          <div className="inline-block rounded-full mb-7 px-7 py-[10px]"
            style={{ border: '1px solid rgba(210,205,191,0.5)', background: 'rgba(255,255,255,0.1)' }}>
            <span className="font-chivoMono uppercase tracking-[4px] text-[13px] md:text-[15px]" style={{ color: C.dune }}>
              {hero.dateBadge || info.date || '5–8 November'}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-ttDrugsTrial uppercase leading-none mb-10" style={{
            fontSize: 'clamp(42px, 7vw, 92px)', letterSpacing: '0.04em', color: '#F2EEE6',
          }}>
            {hero.title || 'Mahima Open 2026'}
          </h1>

          {/* CTA */}
          <a href={registerLink} {...ext(registerLink)}
            className="inline-block font-ttDrugsTrial uppercase transition-colors duration-300"
            style={{
              fontSize: 15, letterSpacing: '3.2px', background: C.dune, color: C.panel,
              border: `1px solid ${C.dune}`, padding: '18px 44px',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#F0ECE3'}
            onMouseLeave={e => e.currentTarget.style.background = C.dune}
          >
            {registerButtonText}
          </a>

          {/* Learn more */}
          <a href="#film" className="font-chivoMono uppercase transition-opacity duration-300 mt-6"
            style={{ fontSize: 11, letterSpacing: '4px', color: 'rgba(242,238,230,0.85)' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {hero.learnMoreText || 'Learn More'}
          </a>
        </div>

        {/* Location */}
        <p className="absolute bottom-11 left-1/2 -translate-x-1/2 z-[3] font-chivoMono uppercase whitespace-nowrap"
          style={{ fontSize: 12, letterSpacing: '5px', color: C.dune }}>
          {hero.locationText || info.location || 'Mahima, Sayan Valley, Ubud'}
        </p>
      </section>

      {/* ── Film ── */}
      <div id="film">
        <FilmBlock promoVideo={d.promoVideo} eventInfo={info} hero={hero} />
      </div>

      {/* ── Intro text ── */}
      {(d.categories?.title || d.categories?.description || info.description) && (
        <section className="bg-background px-smallMobile md:px-mediumDesktop py-largeMobile lg:py-largeDesktop">
          <div className="max-w-[780px] mx-auto flex flex-col items-center text-center">
            {d.categories?.title && (
              <h2 className="font-ttDrugsTrial uppercase text-displayMobile md:text-displayDesktop text-olive leading-none mb-smallMobile">
                {d.categories.title}
              </h2>
            )}
            {d.categories?.description && (
              <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-khaki mt-smallMobile">
                {d.categories.description}
              </p>
            )}
            {info.description?.length > 0 && !d.categories?.description && (
              <div className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-khaki mt-smallMobile">
                <PortableText value={info.description} components={editorRule()} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Photos ── */}
      <PhotoCarousel photos={d.photos} />

      {/* ── Bottom CTA ── */}
      <section className="bg-background px-smallMobile md:px-mediumDesktop py-largeMobile lg:py-largeDesktop border-t border-dune">
        <div className="flex flex-col items-center text-center gap-smallMobile">
          <p className="font-chivoMono uppercase text-detailsMobile md:text-detailsDesktop text-khaki tracking-widest">
            Padel &amp; Tennis Tournament
          </p>
          <h2 className="font-ttDrugsTrial uppercase leading-none text-displayMobile md:text-displayDesktop text-olive">
            {ctaRow1}<br />{ctaRow2}
          </h2>
          {(info.totalSpots || info.prizePool) && (
            <p className="font-chivoMono text-bodyMobile md:text-bodyDesktop text-khaki">
              {[
                info.totalSpots ? `${info.totalSpots} spots` : null,
                info.prizePool ? `${info.prizePool} prize pool` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <a href={registerLink} {...ext(registerLink)}
            className="inline-flex items-center justify-center bg-olive hover:bg-umber transition-colors duration-300 px-[40px] md:px-[60px] py-[18px] md:py-[22px] mt-smallMobile"
          >
            <span className="font-chivoMono text-dune uppercase tracking-[3.5px] text-bodyMobile md:text-rectangleButtonDesktop">
              {registerButtonText}
            </span>
          </a>
        </div>
      </section>

    </Layout>
  )
}

export default MahimaOpenPage
